interface AuthUser {
  id: number;
  email: string;
  contactEmail: string | null;
  contactPhone: string | null;
  role: "user" | "worker";
  firstName: string;
  lastName: string | null;
  emailVerified: boolean;
  notificationLanguage: "en" | "lt" | "ru";
  profileImageUrl: string | null;
  countryId: number | null;
  createdAt: string;
  updatedAt: string;
  workerId: number | null;
  workerIsActive: boolean | null;
  workerCreatedAt: string | null;
  workerUpdatedAt: string | null;
}

export function useAuth() {
  const user = useState<AuthUser | null>("user", () => null);
  const loading = useState<boolean>("authLoading", () => false);
  const error = useState<Error | null>("authError", () => null);

  const localeRoute = useLocaleRoute();

  async function refreshSession() {
    const headers = import.meta.server
      ? useRequestHeaders(["cookie"])
      : undefined;

    if (loading.value) return;
    loading.value = true;
    error.value = null;

    try {
      const data = await $fetch<{ user: AuthUser }>("/api/internal/me", {
        credentials: "include",
        headers,
      });

      user.value = data.user || null;
    } catch (e) {
      if ((e as any)?.response?.status !== 401) {
        console.error("User fetch failed: ", e);
        error.value = e instanceof Error ? e : new Error("Unknown error");
      }
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      const headers = import.meta.server
        ? useRequestHeaders(["cookie"])
        : undefined;

      await $fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        headers,
      });
    } catch (e) {
      console.warn("Logout failed: ", e);
      error.value = e instanceof Error ? e : new Error("Logout failed");
    } finally {
      user.value = null;
      await navigateTo(localeRoute("/"));
    }
  }

  const loggedIn = computed(() => !!user.value);

  return {
    user,
    loggedIn,
    refreshSession,
    logout,
    loading,
    error,
  };
}
